import { useNavigate } from '@tanstack/react-router';
import Button from 'components/Button/Button';
import React, { useState } from 'react';
import appHelpers from 'utils/app-urls';

import imgChevron from 'assets/icons/chevron.svg';
import imgMakeDeposit from './assets/img-make-deposit.png';
import imgMakeInvestment from './assets/img-make-investment-circular.png';
import imgUnverified from './assets/img-unverified.png';
import imgVerificationInProgress from './assets/img-verification-in-progress.png';

const cdnURL = appHelpers.getCDNUrl();
const imgTriangleDown = `${cdnURL}/assets/portfolio/triangle-down.svg`;

type InvestmentJourneyStep =
  | 'UNVERIFIED'
  | 'VERIFICATION_IN_PROGRESS'
  | 'VERIFICATION_UNDER_REVIEW'
  | 'MAKE_DEPOSIT'
  | 'MAKE_INVESTMENT'
  | 'INVESTMENT_JOURNEY_COMPLETE';

interface InvestmentJourneyData {
  title: string;
  description: string;
  img?: any;
  imgOffset?: string;
  topText?: string;
  gradient?: string;
  buttonText?: string;
  path?: string;
  onClick?: () => void;
}

const PortfolioInvestmentJourney: React.FC<{
  activeEntry: InvestmentJourneyStep | null;
  isLoading?: boolean;
}> = ({ activeEntry, isLoading, }) => {
  const navigate = useNavigate();
  // const [activeItem, setActiveItem] = useState<InvestmentJourneyData>();
  // const [activeEntry, setActiveEntry] = useState<FeatureContentType>('MAKE_INVESTMENT');

  // TODO: translations
  const data: { [key in InvestmentJourneyStep]?: InvestmentJourneyData } = {
    UNVERIFIED: {
      topText: 'Next Step',
      title: 'Verify Your Account',
      description:
        'Before you can make a deposit and invest, you need to verify your account. Get your mobile phone, ID, and be in a well-lit room to verify your account.',
      img: imgUnverified,
      buttonText: 'Verify Account',
      path: `/verification`,
      onClick: () => navigate({ to: '/verification' }),
      gradient:
        'linear-gradient(160deg, #00000011 0% , #626AFF99 50%, #626AFF 100%)',
    },
    VERIFICATION_IN_PROGRESS: {
      title: 'Verification in Progress',
      topText: 'Next Step',
      gradient:
        'linear-gradient(160deg, #00000011 0% , #626AFF99 50%, #626AFF 100%)',
      img: imgVerificationInProgress,
      imgOffset: '-right-4 -bottom-4',
      description:
        'We’re reviewing your verification submission. This usually takes less than 24 hours.\n\nWe’ll let you know via email when this is complete. Once you’re verified, you can make a deposit and start investing!',
      buttonText: 'Explore investments',
      onClick: () => navigate({ to: '/products' }),
      path: `/products`,
    },
    VERIFICATION_UNDER_REVIEW: {
      title: 'Verification in Progress',
      topText: 'Next Step',
      gradient:
        'linear-gradient(160deg, #00000011 0% , #626AFF99 50%, #626AFF 100%)',
      img: imgVerificationInProgress,
      imgOffset: '-right-4 -bottom-4',
      description:
        'We’re reviewing your verification submission. This usually takes less than 24 hours.\n\nWe’ll let you know via email when this is complete. Once you’re verified, you can make a deposit and start investing!',
      buttonText: 'Explore investments',
      onClick: () => navigate({ to: '/products' }),
      path: `/products`,
    },
    MAKE_DEPOSIT: {
      topText: 'Next Step',
      title: 'Make a Deposit',
      description:
        'To start investing, fund your account\ntoday with a bank or crypto transfer.',
      gradient: 'linear-gradient(160deg,#626AFFAA 0%, #626AFF 100%)',
      buttonText: 'Deposit',
      onClick: () => navigate({ to: '/funds/deposit' }),
      path: `/funds/deposit`,
      img: imgMakeDeposit,
      imgOffset: '-right-4 -bottom-4 max-w-[130px] md:max-w-[100%]',
    },
    MAKE_INVESTMENT: {
      title: 'You’re ready to go!',
      description:
        'Explore our investment options to make your first investment.',
      buttonText: 'Explore investments',
      onClick: () => navigate({ to: '/products' }),
      path: '/products',
      img: imgMakeInvestment,
      imgOffset: '-right-4 -bottom-4',
      gradient:
        'linear-gradient(320deg, #626AFF -30%, #626AFF33 15%, #626AFF33 40%, #626AFF 100%)',
    },
  };

  const activeItem = data?.[activeEntry as InvestmentJourneyStep];

  // useEffect(() => {
  //     console.log(data?.[activeEntry as InvestmentJourneyStep]);
  //     // @ts-ignore
  //     setActiveItem(data?.[activeEntry as InvestmentJourneyStep]);
  // }, [activeEntry, data]);

  const InvestmentJourneyBanner = () => (
    <div
      className="m-0 mt-1 md:m-4 md:ml-1 rounded-none md:rounded-xl"
      style={{ background: activeItem?.gradient, height: 'calc(100% - 2rem)' }}>
      {/* @ts-ignore */}
      {activeEntry === 'PERFORMANCE_GRAPH' ? null : (
        <div className="flex flex-row justify-between h-full w-full overflow-hidden">
          <div className="flex flex-col justify-between flex-shrink h-full p-4 md:p-4 w-7/12 xl:w-6/12">
            <div className="m-0 md:mt-2 xl:mt-20 z-10">
              {activeItem?.topText && (
                <div className="text-header-text">{activeItem?.topText}</div>
              )}
              <div className="text-4xl text-header-text">
                {activeItem?.title}
              </div>
              <div className="hidden md:block text-header-text text-md mt-2 mb-2">
                {activeItem?.description}
              </div>
            </div>

            {activeItem?.onClick && activeItem?.buttonText && (
              <Button
                type="button"
                outlined
                onClick={() => navigate({ to: activeItem?.path as string })}
                className="mb-2"
                style={{
                  backgroundColor: '#FFFFFF33',
                  width: 'fit-content',
                  paddingLeft: '1rem',
                  paddingRight: '1rem',
                }}>
                {activeItem?.buttonText}{' '}
                <img src={imgChevron} alt="action" className="ml-4" />
              </Button>
            )}
          </div>
          <div className="relative xl:min-w-90 flex-shrink-0 xl:-bottom-16 overflow-hidden w-5/12 xl:w-6/12 flex self-end justify-end">
            <img
              alt=""
              src={activeItem?.img}
              className={`w-full xl:w-auto ${activeItem?.imgOffset || ''}`}
              // className={`w-auto ${activeItem?.imgOffset} bottom-0`}
            />
          </div>
        </div>
      )}
    </div>
  );

  const InvestmentJourneyAccordion = () => (
    <div
      className="text-header-text text-3xl mb-4 mt-2 pl-4"
      style={{ background: activeItem?.gradient }}>
      <div
        className="flex flex-row justify-between cursor-pointer">
        {activeItem?.title && (
          <div
            className="text-2xl md:text-3xl py-4 tracking-wide text-left text-header-text"
            dangerouslySetInnerHTML={{
              __html: (activeItem?.title as string) || '',
            }}
            data-testid="investment-journey-title"
          />
        )}
      </div>
      <div
        style={{
          height: 'auto',
          transition: 'height 1s ease-in-out',
          overflow: 'hidden',
        }}
        className="flex flex-col sm:flex-col md:flex-row">
        <div className="flex flex-col">
          <div
            className="my-4 pr-2 text-header-text text-lg md:text-md font-thin z-10 "
            dangerouslySetInnerHTML={{
              __html: activeItem?.description as string,
            }}
          />
          <div className="flex flex-col align-start w-fit-content mb-2">
            {activeItem?.onClick && (
              <Button
                secondary
                onClick={() => activeItem?.onClick?.()}
                className="px-2 py-0"
                style={{ padding: '0rem', minWidth: '12rem' }}>
                <div className="text-sm">
                  {activeItem?.buttonText}&nbsp;&nbsp;
                </div>
                <img src={imgChevron} alt="cta" />
              </Button>
            )}
          </div>
        </div>
        <div
          className="flex flex-col relative self-end flex-grow"
          style={{ minWidth: '40%' }}>
          <img
            src={activeItem?.img}
            className={
              `w-full max-w-473 flex self-end ${activeItem?.imgOffset}` as string
            }
            alt="banner"
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden lg:block lg:flex-col lg:flex-1">
        <InvestmentJourneyBanner />
      </div>
      <div className="block lg:hidden w-full ">
        <InvestmentJourneyAccordion />
      </div>
    </>
  );
};

export default PortfolioInvestmentJourney;
